import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ActionIcon,
  NumberInput,
  SegmentedControl,
  Box,
  Tooltip,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash, IconPercentage, IconClock, IconHelpCircle } from '@tabler/icons-react';
import { AssetBundleItem } from '@/API';
import {
  ExtensionManifestFormValues,
  StateGroupConfig,
  WeightedActionItem,
  RandomSelectionStrategy,
  calculateNormalizedWeights,
} from './types';
import { AssetBundleFileSelect } from './AssetBundleFileSelect';

interface ImageStatesTemplateFormProps {
  form: UseFormReturnType<ExtensionManifestFormValues>;
  imageFiles: string[];
  workspaceId: string;
  botProviderId: string;
  bundle: AssetBundleItem;
  onBundleUpdated?: (updatedBundle: AssetBundleItem) => void;
}

type ImageStateKey =
  | 'imageIdleConfig'
  | 'imageSleepConfig'
  | 'imageThinkingConfig'
  | 'imageSpeakingConfig'
  | 'imageErrorConfig'
  | 'imageClickConfig';

interface ImageStateMeta {
  key: ImageStateKey;
  label: string;
  defaultStrategy: RandomSelectionStrategy;
  defaultInterval: number;
}

const IMAGE_STATES: ImageStateMeta[] = [
  { key: 'imageIdleConfig', label: '待機圖片 (Idle)', defaultStrategy: 'on-complete', defaultInterval: 5 },
  { key: 'imageSleepConfig', label: '休眠圖片 (Sleep)', defaultStrategy: 'on-complete', defaultInterval: 5 },
  { key: 'imageThinkingConfig', label: '思考中圖片 (Thinking)', defaultStrategy: 'on-complete', defaultInterval: 5 },
  { key: 'imageSpeakingConfig', label: '回應說話圖片 (Speaking)', defaultStrategy: 'on-complete', defaultInterval: 5 },
  { key: 'imageErrorConfig', label: '錯誤圖片 (Error)', defaultStrategy: 'on-complete', defaultInterval: 5 },
  { key: 'imageClickConfig', label: '點擊互動圖片 (Click)', defaultStrategy: 'on-state-enter', defaultInterval: 1.5 },
];

const ALLOWED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.ico'];

export function ImageStatesTemplateForm({
  form,
  imageFiles,
  workspaceId,
  botProviderId,
  bundle,
  onBundleUpdated,
}: ImageStatesTemplateFormProps) {
  const handleAddItem = (stateKey: ImageStateKey) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = [...(current.items || [])];
    items.push({
      animation: '',
      weight: 1,
    });
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleRemoveItem = (stateKey: ImageStateKey, index: number) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = current.items.filter((_, i) => i !== index);
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleUpdateItem = (
    stateKey: ImageStateKey,
    index: number,
    patch: Partial<WeightedActionItem>
  ) => {
    const current = form.values[stateKey] as StateGroupConfig;
    const items = current.items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    form.setFieldValue(`${stateKey}.items`, items);
  };

  const handleStrategyChange = (stateKey: ImageStateKey, strategy: RandomSelectionStrategy) => {
    form.setFieldValue(`${stateKey}.strategy`, strategy);
  };

  const handleIntervalChange = (stateKey: ImageStateKey, interval: number | string) => {
    const num = typeof interval === 'number' && interval > 0 ? interval : 5;
    form.setFieldValue(`${stateKey}.interval`, num);
  };

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="sm">
        <Text fw={500} size="sm">
          各狀態多組圖片 / GIF 加權隨機設定
        </Text>

        {IMAGE_STATES.map((stateMeta) => {
          const config = (form.values[stateMeta.key] || {
            strategy: stateMeta.defaultStrategy,
            interval: stateMeta.defaultInterval,
            items: [],
          }) as StateGroupConfig;
          const itemsWithWeights = calculateNormalizedWeights(config.items || []);
          const isMulti = config.items && config.items.length > 1;

          return (
            <Paper key={stateMeta.key} withBorder p="xs" radius="sm" bg="var(--mantine-color-gray-0)">
              <Stack gap="xs">
                <Group justify="space-between" align="center">
                  <Group gap="xs">
                    <Text size="xs" fw={600}>
                      {stateMeta.label}
                    </Text>
                    {isMulti && (
                      <Badge size="xs" variant="light" color="indigo">
                        多圖隨機 ({config.items.length} 組)
                      </Badge>
                    )}
                  </Group>

                  <Group gap="xs">
                    {config.strategy === 'on-complete' && (
                      <NumberInput
                        size="xs"
                        w={110}
                        min={1}
                        max={60}
                        step={1}
                        placeholder="輪播秒數"
                        value={config.interval ?? stateMeta.defaultInterval}
                        onChange={(val) => handleIntervalChange(stateMeta.key, val)}
                        leftSection={<IconClock size={12} />}
                        rightSection={<Text size="10px" c="dimmed">秒</Text>}
                      />
                    )}

                    <Group gap={4} align="center">
                      <SegmentedControl
                        size="xs"
                        value={config.strategy || stateMeta.defaultStrategy}
                        onChange={(val) => handleStrategyChange(stateMeta.key, val as RandomSelectionStrategy)}
                        data={[
                          { label: '循環隨機輪播', value: 'on-complete' },
                          { label: '進入狀態隨機', value: 'on-state-enter' },
                        ]}
                      />
                      <Tooltip
                        multiline
                        w={280}
                        withArrow
                        withinPortal
                        position="top-end"
                        label={
                          <Stack gap={4}>
                            <Text size="xs" fw={700}>
                              隨機選擇策略說明：
                            </Text>
                            <Text size="xs">
                              • <b>循環隨機輪播</b>：每隔設定的秒數週期，自動依權重隨機抽取並輪播切換下一張圖片（適合待機或思考中定時切換多張表情/動作圖）。
                            </Text>
                            <Text size="xs">
                              • <b>進入狀態隨機</b>：僅在進入該狀態（例如從待機切換到思考中、或觸發點擊）時隨機抽取一次圖片，狀態期間固定顯示抽中之圖片。
                            </Text>
                          </Stack>
                        }
                      >
                        <ActionIcon size="xs" variant="subtle" color="gray" aria-label="策略說明">
                          <IconHelpCircle size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>

                    <Button
                      size="compact-xs"
                      variant="light"
                      leftSection={<IconPlus size={12} />}
                      onClick={() => handleAddItem(stateMeta.key)}
                    >
                      新增圖片
                    </Button>
                  </Group>
                </Group>

                {config.items.length === 0 ? (
                  <Box p="xs" ta="center">
                    <Text size="xs" c="dimmed" mb={4}>
                      尚未設定圖片，點擊右上方「新增圖片」以選取
                    </Text>
                  </Box>
                ) : (
                  <Stack gap={6}>
                    {config.items.map((item, idx) => {
                      const weightInfo = itemsWithWeights[idx];
                      const percentage = weightInfo?.percentage ?? 100;

                      return (
                        <Group key={idx} align="center" wrap="nowrap" gap="xs">
                          <Box style={{ flex: 1 }}>
                            <AssetBundleFileSelect
                              size="xs"
                              placeholder="選擇圖片檔案"
                              data={imageFiles}
                              workspaceId={workspaceId}
                              botProviderId={botProviderId}
                              bundle={bundle}
                              allowedExtensions={ALLOWED_IMAGE_EXTENSIONS}
                              pickerTitle={`挑選 ${stateMeta.label}`}
                              onBundleUpdated={onBundleUpdated}
                              value={item.animation || null}
                              onChange={(val) => handleUpdateItem(stateMeta.key, idx, { animation: val || '' })}
                            />
                          </Box>

                          <NumberInput
                            size="xs"
                            w={85}
                            min={1}
                            step={1}
                            placeholder="權重"
                            value={item.weight ?? 1}
                            onChange={(val) =>
                              handleUpdateItem(stateMeta.key, idx, {
                                weight: typeof val === 'number' && val > 0 ? val : 1,
                              })
                            }
                            leftSection={<IconPercentage size={12} />}
                          />

                          {isMulti && (
                            <Badge size="xs" color="teal" variant="light" w={46} style={{ textAlign: 'center' }}>
                              {percentage}%
                            </Badge>
                          )}

                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            onClick={() => handleRemoveItem(stateMeta.key, idx)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}
